import { Box, InputAdornment, InputLabel, TextField } from '@mui/material';
import { ArrowDownIcon } from '~/assets/icons';
import { forwardRef, InputHTMLAttributes, memo, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './FormDatePicker.module.scss';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { Calendar1Icon } from 'lucide-react';

const cx = classNames.bind(styles);

export type BaseDatePickerProps = {
  value?: Date | string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  text:string;
} & (
  | {
      onChange?: (date: Date | null) => void;
      selectRange?: false;
    }
  | {
      onChange?: (dates: [Date | null, Date | null]) => void;
      selectRange: true;
    }
);

type TextFieldInputProps = InputHTMLAttributes<HTMLInputElement>;

export const FormDatePicker = (props: BaseDatePickerProps) => {
  //#region Destructuring Props
  const { value, placeholder, selectRange = false, error, helperText, text } = props;
  //#endregion Destructuring Props

  //#region Declare State
  const [, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>();

  const [isDateInFuture, setIsDateInFuture] = useState(true);

  const [dateText, setDateText] = useState<string>('');
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (value) {
      const date = dayjs(value).toDate();
      const isDateInFuture = dayjs(date).isAfter(new Date());
      if (isDateInFuture) {
        setStartDate(new Date());
        setEndDate(date);
      } else {
        setStartDate(date);
        setEndDate(new Date());
      }
      setIsDateInFuture(isDateInFuture);
      setDateText(dayjs(date).format('MMM D'));
    }
  }, [value]);
  //#endregion Implement Hook

  //#region Handle Function
  const onChange = (endDate: Date | null) => {
    if (props.onChange) props.onChange(endDate as any);
    const isDateInFuture = dayjs(endDate).isAfter(new Date());
    if (endDate) {
      if (isDateInFuture) {
        setStartDate(new Date());
        setEndDate(endDate);
      } else {
        setStartDate(endDate);
        setEndDate(new Date());
      }
    }
    setIsDateInFuture(isDateInFuture);
    setIsOpen(false);
    setDateText(dayjs(endDate).format('MMM D'));
  };

  const onChangeRange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start ? start : undefined);
    setEndDate(end ? end : undefined);

    if (start && end) {
      if (props.onChange) props.onChange(dates as any);
      setIsDateInFuture(false);
      setIsOpen(false);
      setDateText(`${dayjs(start).format('DD/MM/YYYY')} - ${dayjs(end).format('DD/MM/YYYY')}`);
    }
  };
  //#endregion Handle Function

  const TextFieldInput = forwardRef<HTMLInputElement, TextFieldInputProps>(({ onClick }, ref) => (
    <TextField
      ref={ref}
      inputProps={{ readOnly: true }}
      InputProps={{
        endAdornment: <ArrowDownIcon className='w-[13px]' />,
      }}
      onClick={onClick}
      value={dateText}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
    />
  ));

  return (
     <Box className="w-full">
      {text && <InputLabel className={cx("form-input-title")}>{text}</InputLabel>}
          <Box className={cx('base-date-picker-container')}>
        <Box className={cx('base-date-picker-menu', { 'date-in-future': isDateInFuture })}>
          <DatePicker
            selectsMultiple={undefined}
            selectsRange={selectRange as any}
            onChange={selectRange ? onChangeRange : (onChange as any)}
            startDate={startDate}
            endDate={selectRange ? endDate : startDate}
            selected={startDate}
            dateFormat='MMM d'
            calendarStartDay={1}
            showPopperArrow={false}
            customInput={
              <TextField
                value={dateText}
                onClick={() => {
                  setIsOpen(true);
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Calendar1Icon style={{ cursor: "pointer", color: "#999999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#071427",
                    border: "1px solid #111E33",
                    height:"50px",
                    "& fieldset": { borderColor: "#2D3748" },          // border mặc định
                    "&:hover fieldset": { borderColor: "#1976d2" },     // hover
                    "&.Mui-focused fieldset": { borderColor: "#ff5722" }, // focus
                  },
                  "& .MuiInputBase-input": {
                    padding: "10px",
                    fontSize: "14px",
                    color: "#222",
                    gap:"8px",
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Montserrat",
                    fontWeight: 500,
                    fontStyle: "Medium",
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0%",

                    color: "#666",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#F9FBFE",
                  },
                }}
                
              />
            }

            onClickOutside={() => {
              setIsOpen(false);
            }}
            popperPlacement='top-start'
          />
        </Box>
      </Box>
    </Box>

  );
};

export default memo(FormDatePicker);
